/**
 * Sticky action bar for clip selection and export
 */
export default function ActionBar({
  selectedCount,
  maxSelection,
  onExport,
  onAddToCollection,
  collectionFeatureEnabled = false,
  collectionDisabled = false,
  onClear,
  onSelectAll,
  onDeselectAll,
  onInvertSelection,
  onRemoveSelected,
  disabled = false,
}) {
  const canExport = selectedCount > 0 && !disabled;
  const canAddToCollection = selectedCount > 0 && !collectionDisabled && collectionFeatureEnabled;
  /* c8 ignore next */
  const addToCollectionTitle = collectionDisabled && !canAddToCollection ? 'You can only add clips from your own channel' : undefined;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50" role="region" aria-label="Clip selection actions">
      <div className="max-w-7xl mx-auto px-4 pb-4">
        <div className="bg-dark-700 border border-dark-500 rounded-xl shadow-2xl p-4">
          {/* Selection counter and primary actions */}
          <div className="flex items-center justify-between gap-4 mb-3">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/20 text-primary font-bold" aria-hidden="true">
                {selectedCount}
              </div>
              <div aria-live="polite" aria-atomic="true">
                <p className="font-medium text-white">
                  {selectedCount} clip{selectedCount !== 1 ? 's' : ''} selected
                </p>
                <p className="text-sm text-gray-400">
                  {maxSelection - selectedCount} more allowed
                </p>
              </div>
            </div>

            {/* Primary actions */}
            <div className="flex items-center gap-2">
              {/* c8 ignore start */}
              {collectionFeatureEnabled && (
                <button
                  onClick={onAddToCollection}
                  disabled={!canAddToCollection}
                  className={`
                    px-4 py-2.5 rounded-lg font-semibold text-white transition-all
                    ${canAddToCollection
                      ? 'bg-dark-600 hover:bg-dark-500 border border-primary/40 shadow-md'
                      : 'bg-dark-500 text-gray-500 cursor-not-allowed'
                    }
                  `}
                  aria-label={`Add ${selectedCount} selected clip${selectedCount !== 1 ? 's' : ''} to collection`}
                  title={addToCollectionTitle}
                >
                  + Collection
                </button>
              )}
              {/* c8 ignore stop */}
              <button
                onClick={onExport}
                disabled={!canExport}
                className={`
                  px-6 py-2.5 rounded-lg font-semibold text-white transition-all
                  ${canExport
                    ? 'bg-primary hover:bg-primary-hover shadow-lg shadow-primary/25'
                    : 'bg-dark-500 text-gray-500 cursor-not-allowed'
                  }
                `}
                aria-label={`Export ${selectedCount} selected clip${selectedCount !== 1 ? 's' : ''}`}
              >
                Export Clips
              </button>
            </div>
          </div>

          {/* Selection control buttons */}
          <div className="flex items-center gap-2 pt-3 border-t border-dark-500">
            <button
              onClick={onSelectAll}
              className="px-3 py-1.5 text-xs font-medium text-gray-300 hover:text-white hover:bg-dark-600 rounded-sm transition-colors"
              aria-label="Select all clips"
              title="Select all clips"
            >
              Select All
            </button>
            <button
              onClick={onDeselectAll}
              className="px-3 py-1.5 text-xs font-medium text-gray-300 hover:text-white hover:bg-dark-600 rounded-sm transition-colors"
              aria-label="Deselect all clips"
              title="Deselect all clips"
            >
              Deselect All
            </button>
            <button
              onClick={onInvertSelection}
              className="px-3 py-1.5 text-xs font-medium text-gray-300 hover:text-white hover:bg-dark-600 rounded-sm transition-colors"
              aria-label="Invert selection"
              title="Invert selection"
            >
              Invert
            </button>
            <button
              onClick={onRemoveSelected}
              disabled={selectedCount === 0}
              className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${
                selectedCount === 0
                  ? 'text-gray-600 cursor-not-allowed'
                  : 'text-gray-300 hover:text-white hover:bg-dark-600'
              }`}
              aria-label={`Remove ${selectedCount} selected clips`}
              title="Remove selected clips"
            >
              Remove Selected
            </button>
            {selectedCount > 0 && (
              <button
                onClick={onClear}
                className="px-3 py-1.5 text-xs font-medium text-gray-300 hover:text-white hover:bg-dark-600 rounded-sm transition-colors"
                aria-label={`Clear all ${selectedCount} selected clips`}
                title="Clear selection"
              >
                Clear
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
